import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import {
  signExpiringToken,
  verifyExpiringToken,
} from "./shareToken.server";
import { analyzeSnapshot, type SnapshotInput } from "./familySnapshot";

const SessionId = z.string().min(8).max(64);

const SnapshotInputSchema = z.object({
  household: z.object({
    your_age: z.number().int().min(18).max(110),
    spouse_age: z.number().int().min(18).max(110).nullable().optional(),
    dependents: z
      .array(z.object({ age: z.number().int().min(0).max(40) }))
      .max(15)
      .default([]),
    others_dependent_note: z.string().max(500).optional().default(""),
  }),
  income: z.object({
    your_income: z.number().min(0).max(10_000_000),
    spouse_income: z.number().min(0).max(10_000_000).nullable().optional(),
    months_savings: z.number().int().min(0).max(12),
    job_security_rating: z.number().int().min(1).max(5),
  }),
  coverage: z.object({
    life_self: z.number().min(0).max(50_000_000).default(0),
    life_spouse: z.number().min(0).max(50_000_000).default(0),
    has_disability: z.enum(["yes", "no", "unsure"]),
    has_critical_illness: z.enum(["yes", "no", "unsure"]),
    estate_docs: z.array(z.string().max(40)).max(10).default([]),
  }),
  goals: z.array(z.string().max(60)).max(20).default([]),
});

const signToken = (id: string): string => signExpiringToken(id);
const verifyToken = (token: string): string | null =>
  verifyExpiringToken(token);

export const submitSnapshot = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        session_id: SessionId,
        email: z.string().trim().toLowerCase().email().max(255),
        marketing_opt_in: z.boolean().default(true),
        input: SnapshotInputSchema,
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const snapshotInput = data.input as unknown as SnapshotInput;
    const analysis = analyzeSnapshot(snapshotInput);

    const { data: inserted, error } = await supabaseAdmin
      .from("family_snapshots")
      .insert({
        session_id: data.session_id,
        email: data.email,
        household: data.input.household,
        income: data.input.income,
        coverage: data.input.coverage,
        goals: data.input.goals,
        computed_gap: analysis.gap,
        protection_score: analysis.protection_score,
        recommended_actions: analysis.recommendations,
        marketing_opt_in: data.marketing_opt_in,
        completed_at: new Date().toISOString(),
      } as never)
      .select("id")
      .single();

    if (error || !inserted) {
      return { ok: false as const, error: error?.message ?? "Failed to save." };
    }

    // TODO: send-snapshot-email — wire Resend + PDF attachment when available.
    console.log(
      `[send-snapshot-email] queue snapshot ${inserted.id} to ${data.email}`,
    );

    return { ok: true as const, token: signToken(inserted.id) };
  });

export const loadSnapshot = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ token: z.string().min(8).max(300) }).parse(input),
  )
  .handler(async ({ data }) => {
    const id = verifyToken(data.token);
    if (!id) return { ok: false as const, error: "Link expired or invalid." };
    const { data: row, error } = await supabaseAdmin
      .from("family_snapshots")
      .select(
        "id, email, household, income, coverage, goals, computed_gap, protection_score, recommended_actions, shared_count, completed_at, created_at",
      )
      .eq("id", id)
      .maybeSingle();
    if (error || !row) return { ok: false as const, error: "Snapshot not found." };
    return { ok: true as const, snapshot: row };
  });

export const incrementShare = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ token: z.string().min(8).max(300) }).parse(input),
  )
  .handler(async ({ data }) => {
    const id = verifyToken(data.token);
    if (!id) return { ok: false as const, error: "Invalid token." };
    const { data: row } = await supabaseAdmin
      .from("family_snapshots")
      .select("shared_count")
      .eq("id", id)
      .maybeSingle();
    const next = (row?.shared_count ?? 0) + 1;
    const { error } = await supabaseAdmin
      .from("family_snapshots")
      .update({ shared_count: next } as never)
      .eq("id", id);
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const, shared_count: next };
  });